import { TrainingCourseFilters } from '@/components/TrainingCourseFilterDrawer';
import {
  EMPTY_TRAINING_STUDENT_FILTERS,
  TrainingStudentFilters,
} from '@/components/TrainingStudentFilterDrawer';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/constants';
import { useGetTrainingCourseFilters } from '@/hooks/useGetTrainingCourseFilters';
import { ITrainingCourseFiltersResponse } from '@/types/views/trainingCourse.types';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import { createContext, ReactNode, useContext, useMemo, useReducer } from 'react';

// --- Courses ---

const EMPTY_TRAINING_COURSE_FILTERS: TrainingCourseFilters = {
  providers: [],
  regions: [],
  faculties: [],
  courseTypes: [],
  languages: [],
  priceMin: null,
  priceMax: null,
  hasEnrolments: false,
};

interface ManageCoursesFilterState {
  search: string;
  paginationModel: GridPaginationModel;
  sortBy: string;
  orderBy: 'asc' | 'desc';
  filters: TrainingCourseFilters;
  viewMode: 'grid' | 'list';
}

type ManageCoursesFilterAction =
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_PAGINATION'; payload: GridPaginationModel }
  | { type: 'SET_SORT'; payload: { sortBy: string; orderBy: 'asc' | 'desc' } }
  | { type: 'SET_FILTERS'; payload: TrainingCourseFilters }
  | { type: 'SET_VIEW_MODE'; payload: 'grid' | 'list' }
  | { type: 'RESET' };

const INITIAL_MANAGE_COURSES_FILTER_STATE: ManageCoursesFilterState = {
  search: '',
  paginationModel: { page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE },
  sortBy: 'title',
  orderBy: 'asc',
  filters: EMPTY_TRAINING_COURSE_FILTERS,
  viewMode: 'grid',
};

function manageCoursesFilterReducer(
  state: ManageCoursesFilterState,
  action: ManageCoursesFilterAction
): ManageCoursesFilterState {
  switch (action.type) {
    case 'SET_SEARCH':
      return {
        ...state,
        search: action.payload,
        paginationModel: { ...state.paginationModel, page: 0 },
      };
    case 'SET_PAGINATION':
      return { ...state, paginationModel: action.payload };
    case 'SET_SORT':
      return {
        ...state,
        sortBy: action.payload.sortBy,
        orderBy: action.payload.orderBy,
        paginationModel: { ...state.paginationModel, page: 0 },
      };
    case 'SET_FILTERS':
      return {
        ...state,
        filters: action.payload,
        paginationModel: { ...state.paginationModel, page: 0 },
      };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };
    case 'RESET':
      return INITIAL_MANAGE_COURSES_FILTER_STATE;
    default:
      return state;
  }
}

// --- Students ---

interface ManageStudentsFilterState {
  search: string;
  paginationModel: GridPaginationModel;
  sortBy: string;
  orderBy: 'asc' | 'desc';
  statuses: string[];
  filters: TrainingStudentFilters;
}

type ManageStudentsFilterAction =
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_PAGINATION'; payload: GridPaginationModel }
  | { type: 'SET_SORT'; payload: { sortBy: string; orderBy: 'asc' | 'desc' } }
  | { type: 'SET_STATUSES'; payload: string[] }
  | { type: 'SET_FILTERS'; payload: TrainingStudentFilters }
  | { type: 'RESET' };

const INITIAL_MANAGE_STUDENTS_FILTER_STATE: ManageStudentsFilterState = {
  search: '',
  paginationModel: { page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE },
  sortBy: 'firstName',
  orderBy: 'asc',
  statuses: [],
  filters: EMPTY_TRAINING_STUDENT_FILTERS,
};

function manageStudentsFilterReducer(
  state: ManageStudentsFilterState,
  action: ManageStudentsFilterAction
): ManageStudentsFilterState {
  switch (action.type) {
    case 'SET_SEARCH':
      return {
        ...state,
        search: action.payload,
        paginationModel: { ...state.paginationModel, page: 0 },
      };
    case 'SET_PAGINATION':
      return { ...state, paginationModel: action.payload };
    case 'SET_SORT':
      return {
        ...state,
        sortBy: action.payload.sortBy,
        orderBy: action.payload.orderBy,
        paginationModel: { ...state.paginationModel, page: 0 },
      };
    case 'SET_STATUSES':
      return {
        ...state,
        statuses: action.payload,
        paginationModel: { ...state.paginationModel, page: 0 },
      };
    case 'SET_FILTERS':
      return { ...state, filters: action.payload };
    case 'RESET':
      return INITIAL_MANAGE_STUDENTS_FILTER_STATE;
    default:
      return state;
  }
}

// --- Context ---

interface ManageLearningContextValue {
  manageCoursesFilterState: ManageCoursesFilterState;
  dispatchManageCoursesFilter: React.Dispatch<ManageCoursesFilterAction>;
  manageStudentsFilterState: ManageStudentsFilterState;
  dispatchManageStudentsFilter: React.Dispatch<ManageStudentsFilterAction>;
  courseFiltersData: ITrainingCourseFiltersResponse | undefined;
}

const ManageLearningContext = createContext<ManageLearningContextValue | null>(null);

interface ManageLearningProviderProps {
  children: ReactNode;
}

export function ManageLearningProvider({ children }: ManageLearningProviderProps) {
  const [manageCoursesFilterState, dispatchManageCoursesFilter] = useReducer(
    manageCoursesFilterReducer,
    INITIAL_MANAGE_COURSES_FILTER_STATE
  );
  const [manageStudentsFilterState, dispatchManageStudentsFilter] = useReducer(
    manageStudentsFilterReducer,
    INITIAL_MANAGE_STUDENTS_FILTER_STATE
  );

  // Only used for the (provider-independent) list of available providers, so this fetch omits
  // providerIds on purpose — it must not refetch each time the selected providers/filters change.
  const { data: courseFiltersData } = useGetTrainingCourseFilters();

  const value = useMemo<ManageLearningContextValue>(
    () => ({
      manageCoursesFilterState,
      dispatchManageCoursesFilter,
      manageStudentsFilterState,
      dispatchManageStudentsFilter,
      courseFiltersData,
    }),
    [manageCoursesFilterState, manageStudentsFilterState, courseFiltersData]
  );

  return <ManageLearningContext.Provider value={value}>{children}</ManageLearningContext.Provider>;
}

export function useManageLearning(): ManageLearningContextValue {
  const context = useContext(ManageLearningContext);
  if (!context) {
    throw new Error('useManageLearning must be used within a ManageLearningProvider');
  }
  return context;
}
